export const TIME_PERIOD_OPTIONS = [
  'Today',
  'Yesterday',
  'last 7 days',
  'last 14 days',
  'last 30 days',
  'This month',
  'This year',
  'All time'
];

export const BREAKDOWN_LABELS = {
  SUBSCRIPTIONS: 'Subscriptions',
  CONTENT_PURCHASES: 'Feed PPV',
  TIPS: 'Tips',
  VOICE_VIDEO_CALLS: 'Calls',
  MESSAGE_PURCHASES: 'Message PPV',
  MESSAGE_TIPS: 'Message Tips',
  REFERRALS: 'Referrals',
  VAT_REMITTED: 'VAT Remitted',
  NET_EARNINGS: 'Total Earnings',
  CHARGEBACKS: 'Chargebacks'
};

export const BREAKDOWN_COLORS = {
  [BREAKDOWN_LABELS.SUBSCRIPTIONS]: '#A259FF',
  [BREAKDOWN_LABELS.CONTENT_PURCHASES]: '#A259FF',
  [BREAKDOWN_LABELS.TIPS]: '#3B82F6',
  [BREAKDOWN_LABELS.VOICE_VIDEO_CALLS]: '#F97316',
  [BREAKDOWN_LABELS.MESSAGE_PURCHASES]: '#F59E0B',
  [BREAKDOWN_LABELS.MESSAGE_TIPS]: '#EC4899',
  [BREAKDOWN_LABELS.REFERRALS]: '#22C55E',
  [BREAKDOWN_LABELS.VAT_REMITTED]: null,
  [BREAKDOWN_LABELS.NET_EARNINGS]: null,
  [BREAKDOWN_LABELS.CHARGEBACKS]: null
};

export const BREAKDOWN_FIELD_MAP = {
  subscriptions: BREAKDOWN_LABELS.SUBSCRIPTIONS,
  contentPurchases: BREAKDOWN_LABELS.CONTENT_PURCHASES,
  tips: BREAKDOWN_LABELS.TIPS,
  voiceVideoCalls: BREAKDOWN_LABELS.VOICE_VIDEO_CALLS,
  messagePurchases: BREAKDOWN_LABELS.MESSAGE_PURCHASES,
  messageTips: BREAKDOWN_LABELS.MESSAGE_TIPS,
  referrals: BREAKDOWN_LABELS.REFERRALS,
  vatRemitted: BREAKDOWN_LABELS.VAT_REMITTED,
  netEarnings: BREAKDOWN_LABELS.NET_EARNINGS,
  chargebacks: BREAKDOWN_LABELS.CHARGEBACKS
};

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const COLOR_MAP = {
  "Subscriptions": "#A259FF", // purple
  "Content Purchases": "#A259FF", // purple
  "Feed PPV": "#A259FF", // purple
  "Tips": "#3B82F6", // blue
  "Voice & Video Calls": "#F97316", // orange
  "Calls": "#F97316", // orange
  "Message Purchases": "#F59E0B", // yellowish orange
  "Message PPV": "#F59E0B", // yellowish orange
  "Message Tips": "#EC4899", // pink
  "Referrals": "#22C55E" // green
};

// Tax Info constants
export const FEDERAL_TAX_CLASSIFICATIONS = [
  { label: 'Individual/sole proprietor or single-member LLC', value: 'Individual' },
  { label: 'C Corporation', value: 'C Corporation' },
  { label: 'S Corporation', value: 'S Corporation' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'Trust/estate', value: 'Trust/estate' },
  { label: 'Other', value: 'Other' },
];

export const TIN_TYPE_SSN = 'Social Security Number (SSN)';
export const TIN_TYPE_ITIN = 'Individual Taxpayer Identification Number (ITIN)';
export const TIN_TYPE_EIN = 'Employer Identification Number (EIN)';
export const TIN_TYPE_SSN_VALUE = 'SSN';
export const TIN_TYPE_ITIN_VALUE = 'ITIN';
export const TIN_TYPE_EIN_VALUE = 'EIN';

export const TIN_TYPES = [
  { label: TIN_TYPE_SSN, value: TIN_TYPE_SSN_VALUE },
  { label: TIN_TYPE_ITIN, value: TIN_TYPE_ITIN_VALUE },
  { label: TIN_TYPE_EIN, value: TIN_TYPE_EIN_VALUE },
];

// Tax Info helpers
export const getTinPlaceholder = (tinType) => {
  if (!tinType) return "Select TIN type first";
  if (tinType === TIN_TYPE_SSN_VALUE) return "XXX-XX-XXXX";
  if (tinType === TIN_TYPE_EIN_VALUE) return "XX-XXXXXXX";
  if (tinType === TIN_TYPE_ITIN_VALUE) return "9-XX-XXXXX";
  return "Enter 9-digit number";
};

export const formatTinNumber = (value, type) => {
  const digitsOnly = String(value ?? '').replace(/\D/g, '');
  if (!type) return String(value ?? '');

  if (type === TIN_TYPE_SSN_VALUE) {
    if (digitsOnly.length <= 3) return digitsOnly;
    if (digitsOnly.length <= 5) return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 5)}-${digitsOnly.slice(5, 9)}`;
  }

  if (type === TIN_TYPE_EIN_VALUE) {
    if (digitsOnly.length <= 2) return digitsOnly;
    return `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 9)}`;
  }

  return digitsOnly.slice(0, 9);
};

export const shouldSuppressLoadError = (message) => {
  if (typeof message !== 'string') return true;
  // Many backends return 404 for "not yet created", which is not actionable for the user here.
  return /not found/i.test(message);
};

export const DEFAULT_FORM_VALUES = {
  firstName: '',
  lastName: '',
  businessName: '',
  federalTaxClassification: '',
  otherTaxClassification: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  tinType: '',
  tinNumber: '',
  signature: '',
  date: '',
  certification: false,
};

// W-8 form default values and API mapper
export const DEFAULT_W8_FORM_VALUES = {
  firstName: '',
  lastName: '',
  countryOfCitizenship: '',
  otherCountryOfCitizenship: '',
  permanentStreetAddress: '',
  permanentCity: '',
  permanentStateProvince: '',
  permanentPostalCode: '',
  permanentCountry: '',
  mailingStreetAddress: '',
  mailingCity: '',
  mailingStateProvince: '',
  mailingPostalCode: '',
  mailingCountry: '',
  usTaxpayerId: '',
  foreignTaxId: '',
  ftinNotRequired: false,
  referenceNumber: '',
  countryOfResidenceForTaxTreaty: '',
  otherCountryOfResidenceForTaxTreaty: '',
  articleAndParagraph: '',
  rateOfWithholding: 0,
  typeOfIncome: '',
  additionalConditionsForTreatyBenefits: '',
  digitalSignature: '',
  signatureDate: '',
  capacityInWhichActing: '',
  certification: false,
};

/** W-8BEN form validity period (years) - form must be renewed after this. */
export const W8_FORM_VALIDITY_YEARS = 3;

/** Returns form values for W-8 form reset: from API data when present, otherwise defaults. */
export const getW8FormResetValues = (hasFormData, formData, defaults = DEFAULT_W8_FORM_VALUES) =>
  hasFormData ? buildW8FormValuesFromApi(formData, defaults) : defaults;

/** Build W-8 form values from API response. */
export const buildW8FormValuesFromApi = (formData, defaults = DEFAULT_W8_FORM_VALUES) => ({
  firstName: formData?.firstName ?? defaults.firstName,
  lastName: formData?.lastName ?? defaults.lastName,
  countryOfCitizenship: formData?.countryOfCitizenship ?? defaults.countryOfCitizenship,
  otherCountryOfCitizenship: formData?.otherCountryOfCitizenship ?? defaults.otherCountryOfCitizenship,
  permanentStreetAddress: formData?.permanentStreetAddress ?? defaults.permanentStreetAddress,
  permanentCity: formData?.permanentCity ?? defaults.permanentCity,
  permanentStateProvince: formData?.permanentStateProvince ?? defaults.permanentStateProvince,
  permanentPostalCode: formData?.permanentPostalCode ?? defaults.permanentPostalCode,
  permanentCountry: formData?.permanentCountry ?? defaults.permanentCountry,
  mailingStreetAddress: formData?.mailingStreetAddress ?? defaults.mailingStreetAddress,
  mailingCity: formData?.mailingCity ?? defaults.mailingCity,
  mailingStateProvince: formData?.mailingStateProvince ?? defaults.mailingStateProvince,
  mailingPostalCode: formData?.mailingPostalCode ?? defaults.mailingPostalCode,
  mailingCountry: formData?.mailingCountry ?? defaults.mailingCountry,
  usTaxpayerId: formData?.usTaxpayerId ?? defaults.usTaxpayerId,
  foreignTaxId: formData?.foreignTaxId ?? defaults.foreignTaxId,
  ftinNotRequired: formData?.ftinNotRequired ?? defaults.ftinNotRequired,
  referenceNumber: formData?.referenceNumber ?? defaults.referenceNumber,
  countryOfResidenceForTaxTreaty: formData?.countryOfResidenceForTaxTreaty ?? defaults.countryOfResidenceForTaxTreaty,
  otherCountryOfResidenceForTaxTreaty: formData?.otherCountryOfResidenceForTaxTreaty ?? defaults.otherCountryOfResidenceForTaxTreaty,
  articleAndParagraph: formData?.articleAndParagraph ?? defaults.articleAndParagraph,
  rateOfWithholding: formData?.rateOfWithholding ?? defaults.rateOfWithholding,
  typeOfIncome: formData?.typeOfIncome ?? defaults.typeOfIncome,
  additionalConditionsForTreatyBenefits: formData?.additionalConditionsForTreatyBenefits ?? defaults.additionalConditionsForTreatyBenefits,
  digitalSignature: formData?.digitalSignature ?? defaults.digitalSignature,
  signatureDate: formData?.signatureDate ?? defaults.signatureDate,
  capacityInWhichActing: formData?.capacityInWhichActing ?? defaults.capacityInWhichActing,
  certification: formData?.certification ?? defaults.certification,
});

// Build Tax Info form values from API response
export const buildW9FormValuesFromApi = (formData, defaults = DEFAULT_FORM_VALUES) => ({
  firstName: formData?.firstName || defaults.firstName,
  lastName: formData?.lastName || defaults.lastName,
  businessName: formData?.businessName || defaults.businessName,
  federalTaxClassification:
    formData?.federalTaxClassification || defaults.federalTaxClassification,
  otherTaxClassification: formData?.otherTaxClassification || defaults.otherTaxClassification,
  address: formData?.address || defaults.address,
  city: formData?.city || defaults.city,
  state: formData?.state || defaults.state,
  zipCode: formData?.zipCode || defaults.zipCode,
  tinType: formData?.tinType || defaults.tinType,
  tinNumber: formData?.tinNumber || defaults.tinNumber,
  certification: defaults.certification,
});

export const ATTRIBUTE_LABELS = {
  BANK_ACCOUNT_NUMBER: "Bank Account Number",
  BANK_ROUTING_NUMBER: "Bank Routing Number",
  BANK_ACCOUNT_TYPE: "Bank Account Type"
};

export const PAYMENT_METHOD_MAP = {
  BANK_DEPOSIT: 'BANK_DEPOSIT',
  CRYPTOCURRENCY: 'CRYPTOCURRENCY',
  MANUAL: 'Manual',
};

export const formatPaymentMethod = (deliveryType, payerName) => {
  if (deliveryType === PAYMENT_METHOD_MAP.BANK_DEPOSIT) {
    return 'ACH bank deposit';
  } else if (deliveryType === PAYMENT_METHOD_MAP.CRYPTOCURRENCY && payerName) {
    // Extract currency name from strings like "USDC - Ethereum(ERC20)"
    const currencyName = payerName.split(' - ')[0].trim();
    return `${currencyName} cryptocurrency`;
  } else if (deliveryType === PAYMENT_METHOD_MAP.MANUAL) {
    return 'Manual Payout';
  }
  return 'Payment Method';
};

export const CRYPTO_CURRENCY_NAME_MAP = {
  'Ethereum': 'ETH',
  'Solana': 'SOL',
  'Litecoin': 'LTC',
  'USDC - Ethereum(ERC20)': 'USDC',
  'USDT - Ethereum(ERC20)': 'USDT',
  'USDT - Tron(TRC20)': 'USDT'
};

export const getCryptoCurrencyType = (payerName) => {
  if (!payerName) return payerName;
  return CRYPTO_CURRENCY_NAME_MAP[payerName] || payerName;
};

// Crypto payer name display mapping
// If the payer name matches one of the keys below, we show the mapped value.
// If it does not match, we simply return the original payer name.
const CRYPTO_PAYER_NAME_DISPLAY_MAP = {
  'Ethereum': 'ETH',
  'Solana': 'SOL',
  'Litecoin': 'LTC',
  'USDC - Ethereum(ERC20)': 'USDC (ERC20)',
  'USDT - Ethereum(ERC20)': 'USDT (ERC20)',
  'USDT - Tron(TRC20)': 'USDT (TRC20)',
};

export const getCryptoPayerDisplayName = (payerName) => {
  if (!payerName) return payerName;
  return CRYPTO_PAYER_NAME_DISPLAY_MAP[payerName] || payerName;
};

export const CRYPTO_CURRENCY_ICON = {
  BTC: 'fa-brands fa-bitcoin bank-account-bitcoin-icon',
  ETH: 'fa-brands fa-ethereum bank-account-ethereum-icon',
  LTC: 'fa-brands fa-ethereum bank-account-ethereum-icon',
  USDC: 'fa-brands fa-ethereum bank-account-ethereum-icon',
  USDT: 'fa-brands fa-ethereum bank-account-ethereum-icon',
};

export const usStatesForW9Form_not_used = [
  { code: 'AA', name: 'Armed Forces Americas' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' }
];